import m, {type Child, type Children} from '@bitstillery/mithril'

import {Link} from '@bitstillery/common/components/cms/link/link'
import {present} from '@bitstillery/common/lib/utils'

/**
 * Inline markup inside CMS copy.
 *
 * Placeholder translations are authored as plain strings, but editors (and the translation pipeline)
 * routinely put a bit of inline markup in them — `<i>CBS</i> - Centraal Bureau voor de Statistiek`,
 * a `<br>`, an `<a href>` to a vendor's privacy page. Mithril escapes string children, so that markup
 * used to render as literal text.
 *
 * The CMS text components (`Paragraph`, `Heading`, `Text`, `List`, `Quote`) run their string children
 * through `rich_text()`, which parses a small allowlist of *inline* tags into real vnodes. Nothing is
 * ever passed to `m.trust`: unknown tags stay literal text, so a placeholder can't inject markup —
 * the worst an editor can do is see their tag printed verbatim.
 *
 * Block structure stays the catalog's job: authors compose `Paragraph` / `List` / `Heading`, they do
 * not write `<p>` or `<ul>` in a placeholder.
 */

/** Inline tags an editor may use inside a placeholder translation. Everything else stays literal. */
const INLINE_TAG = /<(\/?)(i|em|b|strong|br|a)(\s[^<>]*?)?\s*(\/?)>/gi

/** Schemes an `<a href>` in copy may point at; anything else drops the anchor but keeps its text. */
const SAFE_HREF = /^(https?:\/\/|mailto:|tel:|\/|#)/i

const ENTITIES: Record<string, string> = {
    amp: '&',
    apos: "'",
    gt: '>',
    hellip: '…',
    lt: '<',
    mdash: '—',
    ndash: '–',
    nbsp: '\u00a0',
    quot: '"',
}

/** `&nbsp;` / `&amp;` / `&#8212;` in authored copy — decoded so it reads as the character it names. */
function decode_entities(text: string): string {
    if (!text.includes('&')) {
        return text
    }
    return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (raw, body: string) => {
        if (body.startsWith('#')) {
            const code =
                body[1] === 'x' || body[1] === 'X' ? Number.parseInt(body.slice(2), 16) : Number.parseInt(body.slice(1), 10)
            return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : raw
        }
        return ENTITIES[body.toLowerCase()] ?? raw
    })
}

/** Read one attribute out of a raw tag's attribute text (`href="/x" target="_blank"`). */
function attribute(attrs: string, name: string): string | undefined {
    const match = new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s"'>]+))`, 'i').exec(attrs)
    if (!match) {
        return undefined
    }
    // Only one of the three quoting alternatives matched; the others are undefined groups.
    const groups: (string | undefined)[] = match
    return decode_entities(groups[2] ?? groups[3] ?? groups[4] ?? '')
}

type Token =
    | {type: 'text'; value: string}
    | {type: 'void'; tag: string}
    | {type: 'open'; tag: string; attrs: string; raw: string}
    | {type: 'close'; tag: string; raw: string}

function tokenize(source: string): Token[] {
    const tokens: Token[] = []
    let cursor = 0
    for (const match of source.matchAll(INLINE_TAG)) {
        const index = match.index
        if (index > cursor) {
            tokens.push({type: 'text', value: source.slice(cursor, index)})
        }
        cursor = index + match[0].length
        const tag = present(match[2], 'inline tag name').toLowerCase()
        if (tag === 'br') {
            tokens.push({type: 'void', tag})
        } else if (match[1] === '/') {
            tokens.push({type: 'close', raw: match[0], tag})
        } else if (match[4] === '/') {
            // `<em/>` carries no text — nothing to emphasise, so leave it as the author typed it.
            tokens.push({type: 'text', value: match[0]})
        } else {
            tokens.push({attrs: match[3] ?? '', raw: match[0], tag, type: 'open'})
        }
    }
    if (cursor < source.length) {
        tokens.push({type: 'text', value: source.slice(cursor)})
    }
    return tokens
}

/**
 * One parsed inline tag as a vnode. `i`/`em` and `b`/`strong` render what `<Text variant>` renders,
 * so authored markup and catalog components look identical; an `a` without a usable href degrades to
 * its own children.
 */
function element(tag: string, attrs: string, children: Child[]): Children {
    switch (tag) {
        case 'i':
        case 'em':
            return m('em', {className: 'cms-text cms-text-italic'}, children)
        case 'b':
        case 'strong':
            return m('strong', {className: 'cms-text cms-text-bold'}, children)
        case 'a': {
            const href = attribute(attrs, 'href')
            if (!href || !SAFE_HREF.test(href)) {
                return children
            }
            return m(Link, {href, target: attribute(attrs, 'target')}, children)
        }
        default:
            return children
    }
}

/** Children as one child: an array becomes the fragment Mithril would have normalized it into. */
function as_child(children: Children): Child {
    return Array.isArray(children) ? m.fragment(null, children) : children
}

interface Frame {
    attrs: string
    children: Child[]
    raw: string
    tag: string
}

function parse(source: string): Child[] {
    const root: Child[] = []
    const stack: Frame[] = []
    const current = () => stack[stack.length - 1]?.children ?? root

    for (const token of tokenize(source)) {
        switch (token.type) {
            case 'text':
                current().push(decode_entities(token.value))
                break
            case 'void':
                current().push(m('br'))
                break
            case 'open':
                stack.push({attrs: token.attrs, children: [], raw: token.raw, tag: token.tag})
                break
            case 'close': {
                const open = stack[stack.length - 1]
                if (!open || open.tag !== token.tag) {
                    // Stray or crossed closing tag — show it rather than guessing what was meant.
                    current().push(token.raw)
                    break
                }
                stack.pop()
                current().push(as_child(element(open.tag, open.attrs, open.children)))
                break
            }
        }
    }

    // Never closed: emit the opening tag verbatim and keep the text that followed it.
    while (stack.length > 0) {
        const open = present(stack.pop(), 'unclosed tag frame')
        current().push(open.raw, ...open.children)
    }
    return root
}

/**
 * CMS copy with its inline markup rendered. Strings without markup pass straight through, so the
 * common case costs one `includes` check; vnodes are returned untouched.
 */
export function rich_text(value: Children): Children {
    if (typeof value === 'string') {
        return value.includes('<') || value.includes('&') ? parse(value) : value
    }
    if (Array.isArray(value)) {
        return value.map((child) => as_child(rich_text(child)))
    }
    return value
}
